import { useState, useEffect, useMemo, useRef } from "react";
import { message, Spin } from "antd";
import { useFilters } from "../hooks/useFilters";
import { useDebounce } from "../hooks/useDebounce";
import { usePackageModal } from "../hooks/usePackageModal";
import { PackageDetailModal } from "components/verification/PackageDetailModal/PackageDetailModal";
import { PackageMessageModal } from "components/verification/PackageMessageModal/PackageMessageModal";
import { useTrainerPackageData } from "../hooks/useTrainerPackageData";
import { FilterBar } from "components/shared/FilterBar/FilterBar";
import { SearchInput } from "components/shared/SearchInput/SearchInput";
import { PackageTable } from "components/verification/PackageTable/PackageTable";
import { PackagePagination } from "components/verification/PackagePagination/PackagePagination";
import { buildTrainerPackageFilterFields } from "../configs/trainerPackageFilter.config";

const normalizeCategory = (cat: string): string => {
  return cat.trim().toLowerCase().replace(/\s+/g, " ");
};

export default function TrainerPackageVerificationPage() {
  const [page, setPage] = useState<number>(1);
  const limit = 10;
  const { filters, updateFilter } = useFilters({
    search: "",
    verified_package: undefined as boolean | undefined,
    category: "",
    is_active: "",
  });

  // Debounce search
  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedSearch = useDebounce(searchInput, 300);

  // Khi debouncedSearch thay đổi, cập nhật filter và reset page
  useEffect(() => {
    updateFilter("search", debouncedSearch);
    setPage(1);
  }, [debouncedSearch, updateFilter]);

  const { packages, total, loading, error, fetchPackages } =
    useTrainerPackageData(filters, page, limit);

  const modal = usePackageModal();

  // State category options
  const [allCategoryOptions, setAllCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (packages.length === 0) return;
    if (
      filters.category &&
      filters.category.trim() !== "" &&
      !isInitialLoad.current
    )
      return;

    const categorySet = new Set<string>();
    packages.forEach((pkg) => {
      if (pkg.categories && pkg.categories.trim() !== "") {
        const normalized = normalizeCategory(pkg.categories);
        categorySet.add(normalized);
      }
    });
    const uniqueCategories = Array.from(categorySet).map((cat) => ({
      label: cat
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      value: cat,
    }));
    setAllCategoryOptions(uniqueCategories);
    isInitialLoad.current = false;
  }, [packages, filters.category]);

  const filterFields = useMemo(
    () => buildTrainerPackageFilterFields(allCategoryOptions),
    [allCategoryOptions],
  );

  useEffect(() => {
    if (!loading && total > 0) {
      const lastPage = Math.ceil(total / limit);
      if (page > lastPage) {
        setPage(lastPage);
      }
    }
  }, [loading, total, page, limit]);

  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    updateFilter(key, value);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <SearchInput
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search by title or description..."
          width="280px"
        />
        <FilterBar
          filters={filters}
          fields={filterFields}
          onChange={handleFilterChange}
          ignoreActiveKeys={["search"]}
        />
      </div>

      {error && <div style={{ marginBottom: 16, color: "red" }}>{error}</div>}

      <Spin spinning={loading}>
        <PackageTable packages={packages} modal={modal} />
      </Spin>

      {total > 0 && (
        <PackagePagination
          page={page}
          totalPages={totalPages}
          setPage={setPage}
          total={total}
        />
      )}

      {modal.showDetailModal && modal.selectedPkg && (
        <PackageDetailModal
          pkg={modal.selectedPkg}
          onClose={modal.closeDetail}
          onSendMessage={modal.openMessage}
          refreshPackages={() => {
            fetchPackages();
            modal.closeDetail();
          }}
        />
      )}

      {modal.showMessageModal && (
        <PackageMessageModal
          messageText={modal.messageText}
          setMessageText={modal.setMessageText}
          onSend={() => {
            message.success("Message sent: " + modal.messageText);
            modal.closeMessage();
          }}
          onClose={modal.closeMessage}
        />
      )}
    </div>
  );
}
